import * as React from "react";
import {
  CourseInstructionType,
  CourseUnitView,
  ICourseInstruction,
  ICourseInstructionContentPart,
  IAssessment,
  ICourseComponent,
  ICourseEditOperations,
  IQuestionPart,
  IGridPart,
} from "../components/course-author/types";
import styled from "styled-components";
import { DraggablePlaceholder } from "../components/pages/CourseAuthor/PreviewTab/DraggableContainer.styles";

export type DropZoneType = "unit" | "instruction" | "question" | "content";

export interface IDropZoneProps {
  children?: React.ReactNode;
  dropPosition: number | undefined;
  dropZoneType: DropZoneType;
  editOperations: ICourseEditOperations;
  newInstructionType: CourseInstructionType | null;
  path: CourseUnitView[] | [];
  instructionSlide?: ICourseInstruction | IAssessment;
}

interface IDropFunctions {
  content: (part: ICourseInstructionContentPart) => void;
  instruction: (courseComponentData: ICourseComponent) => void;
  question: (questionPart: IQuestionPart) => void;
  unit: (courseComponentData: ICourseComponent) => void;
  gridContent: (gridPart: IGridPart, part: any) => void;
}

const Placeholder = styled(DraggablePlaceholder)`
  position: static;
`;

const crossDraggablePlaceholderId = "cross-draggable-placeholder";

const prepareCourseCompData = (
  data: ICourseComponent,
  newInstructionType: CourseInstructionType | null
): ICourseComponent => {
  if (!newInstructionType) {
    return data;
  }
  const newInstTypeContent = { ...data.content, type: newInstructionType };
  return { ...data, content: newInstTypeContent } as ICourseComponent;
};

export const useDragAndDrop = () => {
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [isOver, setIsOver] = React.useState<boolean>(false);
  const [isOverPlaceholder, setIsOverPlaceholder] = React.useState<boolean>(false);
  const destinationTabId = parseFloat(sessionStorage.tabId) || 0;

  const handleDragging = (dragging: boolean) => setIsDragging(dragging);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropZoneType: DropZoneType, dropFuns: IDropFunctions) => {
    e.preventDefault();
    e.dataTransfer.effectAllowed = "move";
    const { data, type, sourceTabId } = JSON.parse(e.dataTransfer.getData("text/plain"));

    if (Number(sourceTabId) === destinationTabId) {
      setIsOver(false);
      return;
    }

    // ensure we are copying the same type of component/part
    if (type === dropZoneType) {
      if (Array.isArray(data) && data[0].tag === "grid") {
        const gridPart = data[0] as IGridPart;
        const childParts = data.slice(1);
        dropFuns[`gridContent`](gridPart, childParts);
      } else {
        dropFuns[type](data);
      }
    } else {
      window.alert("Error while copying the content. Ensure you are copying the same type of content.");
    }

    setIsOver(false);
    handleDragging(false);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    const targetElement = e.target as HTMLElement;
    if (targetElement.id === crossDraggablePlaceholderId) {
      setIsOverPlaceholder(true);
    }
    setIsOver(true);
  };

  const handleDragLeave = () => (isOverPlaceholder ? setIsOverPlaceholder(false) : setIsOver(false));

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

  return { isDragging, handleDragging, handleDrop, handleDragEnter, isOver, handleDragLeave, handleDragOver };
};

export const DropZone: React.FC<IDropZoneProps> = ({
  children,
  editOperations,
  dropPosition,
  path,
  dropZoneType,
  newInstructionType,
  instructionSlide,
}) => {
  const { handleDrop, handleDragEnter, isOver, handleDragLeave, handleDragOver } = useDragAndDrop();
  const moveOrCopyInstruction = (instructionType: CourseInstructionType | null) => (
    courseComponentData: ICourseComponent
  ) => {
    if (!instructionType) {
      return editOperations.moveOrCopyComponent(path, courseComponentData, dropPosition ?? 0);
    }
    const formattedData = prepareCourseCompData(courseComponentData, instructionType);
    editOperations.moveOrCopyComponent(path, formattedData, dropPosition ?? 0);
  };

  const copyPart = (part: IQuestionPart | ICourseInstructionContentPart) =>
    instructionSlide && editOperations.copyPart(path, instructionSlide)(part, dropPosition);

  const copyGridContent = (gridPart: IGridPart, childParts: ICourseInstructionContentPart[]) => {
    const emptyGridPart = {
      ...gridPart,
      content: {
        ...gridPart.content,
        columns: {
          ...gridPart.content.columns,
          content: gridPart.content.columns.content.map(() => null),
        },
      },
    } as IGridPart;

    instructionSlide && editOperations.copyGridParts(path, instructionSlide)(emptyGridPart, childParts, dropPosition);
  };

  const dropFuns: IDropFunctions = {
    content: copyPart,
    gridContent: copyGridContent,
    instruction: moveOrCopyInstruction(newInstructionType),
    question: copyPart,
    unit: moveOrCopyInstruction(newInstructionType),
  };

  return (
    <div
      // tslint:disable-next-line:jsx-no-lambda
      onDrop={e => handleDrop(e, dropZoneType, dropFuns)}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      style={{ display: "contents" }}
    >
      {isOver && renderDraggablePlaceholder(dropZoneType)}
      {children}
    </div>
  );
};

const renderDraggablePlaceholder = (dropZoneType: DropZoneType) => {
  const heightLookup: { [key in DropZoneType]: number } = {
    content: 6,
    instruction: 3,
    question: 6,
    unit: 4,
  };
  const height = heightLookup[dropZoneType];
  return <Placeholder height={height} id={crossDraggablePlaceholderId} />;
};

export const CancelDragZone: React.FC<{ customStyle?: React.CSSProperties }> = ({ customStyle, children }) => {
  const cancelDrag = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();
  return (
    <div draggable={true} onDragStart={cancelDrag} style={{ ...customStyle }}>
      {children}
    </div>
  );
};
